import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Ticket, X, Power } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  code: '',
  percent: 10,
  expires_at: '',
  max_uses: '',
  one_per_email: false,
  description: '',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-ES');
  } catch {
    return iso;
  }
};

const isExpired = (iso) => {
  if (!iso) return false;
  try {
    return new Date(iso) < new Date();
  } catch {
    return false;
  }
};

export default function DiscountCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/discount-codes`);
      setCodes(r.data || []);
    } catch (e) {
      toast.error('Error cargando códigos: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.code.trim()) {
      toast.error('Introduce un código');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        percent: Number(form.percent),
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        one_per_email: form.one_per_email,
        description: form.description || null,
        active: true,
      };
      await axios.post(`${API}/discount-codes`, payload);
      toast.success('Código creado');
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el código');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/discount-codes/${c.code}`, { active: !c.active });
      load();
    } catch (e) {
      toast.error('No se pudo actualizar');
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`¿Eliminar el código ${code}?`)) return;
    try {
      await axios.delete(`${API}/discount-codes/${code}`);
      toast.success('Código eliminado');
      load();
    } catch (e) {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <Card className="border-0 shadow-2xl mb-6" data-testid="discount-codes-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <Ticket size={20} className="text-pink-600" />
          Códigos de Descuento
        </CardTitle>
        <div className="flex gap-2">
          <Button
            data-testid="discount-refresh"
            onClick={load}
            variant="outline"
            size="sm"
            className="border-slate-300"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button
            data-testid="discount-new-btn"
            onClick={() => setShowForm((s) => !s)}
            size="sm"
            className="bg-pink-600 hover:bg-pink-700 text-white"
          >
            {showForm ? <X size={14} className="mr-1" /> : <Plus size={14} className="mr-1" />}
            {showForm ? 'Cerrar' : 'Nuevo código'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <div
            className="bg-slate-50 rounded-lg p-4 mb-6 grid sm:grid-cols-2 gap-4"
            data-testid="discount-form"
          >
            <div>
              <Label className="text-xs">Código *</Label>
              <Input
                data-testid="discount-code-input"
                value={form.code}
                onChange={(e) => setField('code', e.target.value.toUpperCase())}
                placeholder="VERANO20"
                className="uppercase"
              />
            </div>
            <div>
              <Label className="text-xs">Descuento (%) *</Label>
              <Input
                data-testid="discount-percent-input"
                type="number"
                min="1"
                max="100"
                value={form.percent}
                onChange={(e) => setField('percent', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Caduca el (opcional)</Label>
              <Input
                data-testid="discount-expires-input"
                type="date"
                value={form.expires_at}
                onChange={(e) => setField('expires_at', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Usos máximos (opcional)</Label>
              <Input
                data-testid="discount-maxuses-input"
                type="number"
                min="1"
                value={form.max_uses}
                onChange={(e) => setField('max_uses', e.target.value)}
                placeholder="Ilimitado"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Descripción (opcional)</Label>
              <Input
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Ej: Campaña de verano"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="discount-oneperemail-switch"
                checked={form.one_per_email}
                onCheckedChange={(v) => setField('one_per_email', v)}
              />
              <Label className="text-xs">Un solo uso por email</Label>
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button
                data-testid="discount-save-btn"
                onClick={create}
                disabled={saving}
                className="bg-pink-600 hover:bg-pink-700 text-white"
              >
                {saving ? 'Guardando...' : 'Crear código'}
              </Button>
            </div>
          </div>
        )}

        {codes.length === 0 ? (
          <p className="text-center text-slate-400 py-8 text-sm">
            No hay códigos de descuento. Crea uno con "Nuevo código".
          </p>
        ) : (
          <div className="space-y-2" data-testid="discount-list">
            {codes.map((c) => {
              const expired = isExpired(c.expires_at);
              const usedUp = c.max_uses && c.used_count >= c.max_uses;
              const live = c.active && !expired && !usedUp;
              return (
                <div
                  key={c.code}
                  data-testid={`discount-row-${c.code}`}
                  className="flex flex-wrap items-center justify-between gap-3 border border-slate-100 rounded-lg p-3 hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-slate-900 text-lg">{c.code}</span>
                    <Badge className="bg-pink-100 text-pink-700">{c.percent}% off</Badge>
                    {live ? (
                      <Badge className="bg-green-100 text-green-700">Activo</Badge>
                    ) : expired ? (
                      <Badge className="bg-amber-100 text-amber-700">Caducado</Badge>
                    ) : usedUp ? (
                      <Badge className="bg-slate-200 text-slate-600">Agotado</Badge>
                    ) : (
                      <Badge className="bg-slate-200 text-slate-600">Inactivo</Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      Usos: <strong>{c.used_count}</strong>
                      {c.max_uses ? ` / ${c.max_uses}` : ' (ilimitado)'}
                    </span>
                    <span>Caduca: {fmtDate(c.expires_at)}</span>
                    {c.one_per_email && <span>1×email</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      data-testid={`discount-toggle-${c.code}`}
                      onClick={() => toggleActive(c)}
                      variant="outline"
                      size="sm"
                      className="border-slate-300"
                      title={c.active ? 'Desactivar' : 'Activar'}
                    >
                      <Power size={14} className={c.active ? 'text-green-600' : 'text-slate-400'} />
                    </Button>
                    <Button
                      data-testid={`discount-delete-${c.code}`}
                      onClick={() => remove(c.code)}
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
